export type PlaceDot = 'place_dot_xs' | 'place_dot_s' | 'place_dot_m' | 'place_dot_l';

const SIZES: PlaceDot[] = ['place_dot_xs', 'place_dot_s', 'place_dot_m', 'place_dot_l'];

/** The terrain's own rank of a settlement (`CfgWorlds >> Names`) as a size, or null for any other place. */
function rankOf(type: string | null | undefined): number | null {
  if (!type) return null;
  if (type === 'NameCityCapital') return 3;
  if (type.startsWith('NameCity')) return 2;
  if (type === 'NameVillage') return 1;
  if (type === 'NameLocal') return 0;
  return null;
}

/**
 * The size the houses alone give, against the largest town of the same map: half of its houses or more is a
 * capital's dot, a fifth a city's, about one in seventeen a village's. Read on Altis (production survey v6,
 * 2026-10-19): Kavala 1 011, Pyrgos 771 and Athira 506 the largest; Sofia 338 and Neri 277 the middle; a hamlet
 * under 60.
 */
function byHouses(houses: number, most: number): number {
  const share = houses / most;
  if (share >= 0.5) return 3;
  if (share >= 0.2) return 2;
  if (share >= 0.06) return 1;
  return 0;
}

/**
 * The dot a candidate place is drawn as, by how big a place it is.
 *
 * The base is the terrain's own rank of it (`CfgWorlds >> Names`), a capital
 * the largest and a hamlet or a named spot the smallest — never its radius,
 * which is how far the name reaches on the map: on Altis Gravia, a small town,
 * reaches 734 m and the capital Pyrgos 500 m. Where the survey counted the
 * houses (`houses` on the candidates, the most of any town on the map in
 * `most`), the count sizes the dot, at most one step from the rank's: a
 * village as big as a town is drawn as a town, a city of a few houses as a
 * village, and a capital never smaller than a city (the owner, web card 196,
 * 2026-10-18: «la mida dels punts no segueix la població»). The bounds rise
 * with the rank, so of two places a bigger rank with more houses is never the
 * smaller dot. A place whose kind is not a settlement (a height, a site) keeps
 * the medium dot.
 */
export function dotSizeOf(
  type: string | null | undefined,
  houses?: number | null,
  most?: number | null,
): PlaceDot {
  const rank = rankOf(type);
  if (type && rank === null) return 'place_dot_m';
  if (houses == null || most == null || most <= 0) {
    return rank === null ? 'place_dot_m' : SIZES[rank];
  }
  const size = byHouses(houses, most);
  if (rank === null) return SIZES[size];
  return SIZES[Math.min(Math.max(size, rank - 1, 0), rank + 1, 3)];
}
